import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Button, Card, CardContent, Chip, Grid, MenuItem, Select,
  Step, StepLabel, Stepper, TextField, Typography, makeStyles, Tooltip,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import PrintIcon from '@material-ui/icons/Print'
import SaveIcon from '@material-ui/icons/Save'
import CheckIcon from '@material-ui/icons/Check'
import Layout from '../components/layout/Layout'
import cup from '../assets/pic/arjan_cup.jpg'
import { REPORT_FOOTER_TEXT } from '../assets/reportFooter'
import api from '../api/client'
import { toast } from '../api/toast'
import { gregorianToJalali, jalaliToGregorian, toFarsiDigits, toEnglishDigits } from '../assets/utils'

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    minHeight: 'calc(100vh - 150px)',
    padding: '10px 4px 30px',
    overflow: 'hidden',
    borderRadius: 18,
  },
  bg: {
    position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center',
    opacity: 0.08, filter: 'grayscale(0.15) sepia(0.3)', pointerEvents: 'none', zIndex: 0,
  },
  shell: { position: 'relative', zIndex: 1, maxWidth: 1180, margin: '0 auto' },
  panel: {
    background: '#ffffff',
    borderRadius: 18,
    border: '1px solid #e6e9f6',
    boxShadow: '0 18px 50px rgba(26,35,126,.10)',
    padding: 18,
  },
  hero: {
    position: 'relative', overflow: 'hidden', borderRadius: 14, marginBottom: 14,
    background: 'linear-gradient(120deg, #1a237e 0%, #283593 45%, #3949ab 100%)',
    color: '#fff', padding: '14px 18px',
  },
  heroBg: {
    position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center 45%',
    opacity: 0.28, mixBlendMode: 'luminosity',
  },
  heroContent: { position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  stepBar: { background: '#f7f9ff', borderRadius: 12, padding: '4px 10px', marginBottom: 14, border: '1px solid #e6e9f6' },
  cell: {
    height: 64, borderRadius: 8, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    border: '1px solid #e6e9f5', background: '#fff', userSelect: 'none',
  },
  cellStop: { background: '#ffebee', borderColor: '#ef9a9a' },
  cellApproved: { background: '#e8f5e9', borderColor: '#a5d6a7' },
  cellPending: { background: '#fff8e1', borderColor: '#ffe082' },
  cellHoliday: { background: '#f3e5f5', borderColor: '#ce93d8' },
  cellEmpty: { border: 'none', background: 'transparent', cursor: 'default' },
  th: { background: '#1a237e', color: '#fff', fontSize: 12, padding: '6px 4px', textAlign: 'center' },
  legend: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, marginLeft: 14 },
  dot: { width: 12, height: 12, borderRadius: 3, display: 'inline-block' },
}))

const pad = (n) => String(n).padStart(2, '0')
const isoOf = (gy, gm, gd) => `${gy}-${pad(gm)}-${pad(gd)}`
const jParts = (iso) => toEnglishDigits(gregorianToJalali(iso)).split('/').map(Number)
const gregOf = (jy, jm, jd) => {
  const [gy, gm, gd] = jalaliToGregorian(jy, jm, jd)
  return isoOf(gy, gm, gd)
}
const jLabel = (iso) => (iso ? toFarsiDigits(gregorianToJalali(iso).slice(0, 10)) : '—')

const STAY_KINDS = [
  { value: 'employer', label: 'کارفرما' },
  { value: 'public', label: 'توقف عمومی' },
  { value: 'weather', label: 'شرایط جوی' },
  { value: 'force', label: 'قوهٔ قاهره' },
  { value: 'contractor', label: 'پیمانکار' },
]

const WEEKDAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه']

const DelayPage = () => {
  const classes = useStyles()
  const { id } = useParams()
  const navigate = useNavigate()
  const goBack = () => {
    if (window.history && window.history.length > 1) navigate(-1)
    else navigate('/')
  }
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [step, setStep] = useState(0)
  const [printAll, setPrintAll] = useState(false)
  const [busy, setBusy] = useState(false)
  const [comment, setComment] = useState('')
  const [objLines, setObjLines] = useState([])
  const [objDays, setObjDays] = useState([])
  const [kind, setKind] = useState('employer')
  const [pending, setPending] = useState({})
  const [removed, setRemoved] = useState([])
  const [lineEdits, setLineEdits] = useState({})
  const [holidays, setHolidays] = useState([])
  const _t = new Date()
  const todayIso = `${_t.getFullYear()}-${pad(_t.getMonth() + 1)}-${pad(_t.getDate())}`
  const now = jParts(todayIso)
  const [jy, setJy] = useState(now[0])
  const [jm, setJm] = useState(now[1])

  const load = () => {
    api.get(`contracts/${id}/delay/`)
      .then((r) => { setData(r.data); setError('') })
      .catch((e) => {
        const msg = (e && e.response && e.response.data && e.response.data.detail) || 'خطا در بارگذاری'
        setError(msg)
        setData(null)
      })
  }

  useEffect(() => { load() }, [id])
  useEffect(() => {
    api.get('contracts/holidays/').then((r) => setHolidays((r.data && r.data.holidays) || [])).catch(() => {})
  }, [])

  const stopMap = useMemo(() => {
    const m = {}
    ;((data && data.stops) || []).forEach((s) => { m[s.date] = s })
    return m
  }, [data])
  const holidaySet = useMemo(() => new Set(holidays.map((h) => h.date)), [holidays])

  const monthDays = useMemo(() => {
    const out = []
    for (let d = 1; d <= 31; d++) {
      const g = gregOf(jy, jm, d)
      const p = jParts(g)
      if (p[1] !== jm || p[0] !== jy) break
      out.push({ day: d, iso: g, dow: new Date(g + 'T00:00:00').getDay() })
    }
    return out
  }, [jy, jm])

  const startCol = monthDays.length ? (monthDays[0].dow + 1) % 7 : 0

  const toggleDay = (isoDate) => {
    if (!data) return
    const canSelect = ((data.report && data.report.actions) || [])
      .some((a) => a.action.indexOf('reject') >= 0)
    if (canSelect) {
      setObjDays((d) => (d.includes(isoDate) ? d.filter((x) => x !== isoDate) : [...d, isoDate]))
      return
    }
    if (!data.access.can_edit) return
    const existing = stopMap[isoDate]
    if (existing || pending[isoDate]) {
      setPending((p) => { const n = { ...p }; delete n[isoDate]; return n })
      if (existing) setRemoved((r) => (r.includes(isoDate) ? r : [...r, isoDate]))
      else setRemoved((r) => (r.includes(isoDate) ? r : [...r, isoDate]))
    } else {
      setRemoved((r) => r.filter((x) => x !== isoDate))
      setPending((p) => ({ ...p, [isoDate]: { kind, reason: '', document_no: '' } }))
    }
  }

  const saveStops = async () => {
    setBusy(true)
    try {
      const days = Object.entries(pending).map(([date, v]) => ({
        date, kind: v.kind, reason: v.reason, document_no: v.document_no,
      }))
      await api.post(`contracts/${id}/delay/stops/`, { days, delete: removed })
      setPending({}); setRemoved([])
      toast('روزهای توقف ذخیره شد', 'success')
      load()
    } catch (e) {
      toast('خطا در ذخیره', 'error')
    } finally { setBusy(false) }
  }

  const verifyStops = async (status) => {
    const dates = ((data && data.stops) || []).filter((s) => s.status === 'pending').map((s) => s.date)
    if (!dates.length) return toast('موردی در انتظار تأیید نیست', 'info')
    setBusy(true)
    try {
      await api.patch(`contracts/${id}/delay/stops/`, { dates, status })
      toast(status === 'approved' ? 'تأیید شد' : 'رد شد', 'success')
      load()
    } catch (e) { toast('خطا', 'error') } finally { setBusy(false) }
  }

  const lineValue = (clauseNo) => {
    if (lineEdits[clauseNo]) return lineEdits[clauseNo]
    const found = ((data && data.lines) || []).find((l) => l.clause_no === clauseNo)
    return found || { clause_no: clauseNo, actual_days: 0, overlap_days: 0, justified: true, document_no: '', remark: '' }
  }

  const setLine = (clauseNo, patch) => {
    setLineEdits((s) => ({ ...s, [clauseNo]: { ...lineValue(clauseNo), ...patch } }))
  }

  const saveLines = async () => {
    setBusy(true)
    try {
      const lines = Object.values(lineEdits)
        .map((l) => ({
          clause_no: l.clause_no,
          title: l.title || (((data && data.clauses) || []).find((c) => c.clause_no === l.clause_no) || {}).title || '',
          actual_days: Number(l.actual_days) || 0,
          overlap_days: Number(l.overlap_days) || 0,
          justified: !!l.justified,
          document_no: l.document_no || '',
          remark: l.remark || '',
        }))
        .filter((l) => l.actual_days > 0 || l.document_no || l.remark)
      await api.post(`contracts/${id}/delay/lines/`, { lines })
      setLineEdits({})
      toast('ردیف‌های تأخیر ذخیره شد', 'success')
      load()
    } catch (e) { toast('خطا در ذخیره', 'error') } finally { setBusy(false) }
  }

  const verifyLines = async (status) => {
    const nos = ((data && data.lines) || []).filter((l) => l.status === 'pending').map((l) => l.clause_no)
    if (!nos.length) return toast('موردی در انتظار تأیید نیست', 'info')
    setBusy(true)
    try {
      await api.patch(`contracts/${id}/delay/lines/`, { clause_nos: nos, status })
      toast(status === 'approved' ? 'تأیید شد' : 'رد شد', 'success')
      load()
    } catch (e) { toast('خطا', 'error') } finally { setBusy(false) }
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Box className={classes.shell}>
          <Box className={classes.panel} style={{ textAlign: 'center', padding: 40 }}>
            <Typography style={{ fontWeight: 800, color: '#b71c1c', marginBottom: 16 }}>{error}</Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack}
              style={{ background: '#eef1ff', color: '#1a237e', fontWeight: 800 }}>
              بازگشت
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  if (!data) {
    return (
      <Box className={classes.root}>
        <Box className={classes.shell}>
          <Box className={classes.panel} style={{ textAlign: 'center', padding: 40 }}>
            <Typography style={{ marginBottom: 16, color: '#5c6bc0' }}>در حال بارگذاری…</Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack}
              style={{ background: '#eef1ff', color: '#1a237e', fontWeight: 800 }}>
              بازگشت
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  const doAction = async (action) => {
    if (action.indexOf('reject') >= 0 && !comment.trim()) {
      return toast('برای ایراد، توضیح الزامی است', 'error')
    }
    setBusy(true)
    try {
      const payload = { action, comment }
      if (action.indexOf('reject') >= 0) {
        payload.objected_lines = objLines
        payload.objected_days = objDays
      }
      await api.post(`contracts/${id}/delay/report/`, payload)
      toast('انجام شد', 'success')
      setComment('')
      setObjLines([])
      setObjDays([])
      load()
    } catch (e) {
      toast((e && e.response && e.response.data && e.response.data.detail) || 'خطا', 'error')
    } finally { setBusy(false) }
  }

  const { contract, access, summary, report } = data
  const canEdit = access.can_edit
  const canVerify = access.can_verify
  const canSelect = ((report && report.actions) || []).some((a) => a.action.indexOf('reject') >= 0)
  const openLines = (report && report.objections && report.objections.lines) || []
  const dirty = Object.keys(pending).length > 0 || removed.length > 0

  const cellClass = (isoDate) => {
    const openDays = (report.objections && report.objections.days) || []
    if (objDays.includes(isoDate) || openDays.includes(isoDate)) return classes.cellStop
    if (pending[isoDate]) return classes.cellPending
    const s = stopMap[isoDate]
    if (s) {
      const approvedReport = ['consultant_approved', 'supervisor_review', 'final'].indexOf(report.state) >= 0
      if (approvedReport) return classes.cellApproved
      return s.status === 'rejected' ? classes.cellStop : classes.cellPending
    }
    if (holidaySet.has(isoDate)) return classes.cellHoliday
    return ''
  }

  const handlePrint = () => {
    setPrintAll(true)
    setTimeout(() => {
      window.print()
      setTimeout(() => setPrintAll(false), 500)
    }, 80)
  }

  // صورت‌جلسهٔ رسمی (۲۲ ستون) در تبِ جدید — لینکِ امضاشده، بدون نیاز به ورودِ دوباره
  const openMinutes = async () => {
    try {
      const r = await api.get(`contracts/${id}/delay/minutes/`)
      const url = r.data && r.data.print_url
      if (!url) return
      const base = ((api.defaults && api.defaults.baseURL) || '').replace(/\/$/, '')
      window.open(base + url, '_blank', 'noopener')
    } catch (e) {
      toast('خطا در ساخت صورت‌جلسه', 'error')
    }
  }

  return (
    <Layout>
    <Box className={classes.root} id="delay-print">
      <Box className={classes.bg} style={{ backgroundImage: `url(${cup})` }} />
      <Box className={classes.shell}>
        <Box className={classes.panel}>
      <Box className={classes.hero}>
        <Box className={classes.heroBg} style={{ backgroundImage: `url(${cup})` }} />
        <Box className={classes.heroContent}>
          <Button startIcon={<ArrowBackIcon />} onClick={goBack}
            style={{ background: 'rgba(255,255,255,.16)', color: '#fff', fontWeight: 800, textTransform: 'none' }}>
            بازگشت
          </Button>
          <Box>
            <Typography style={{ fontWeight: 800, fontSize: 18 }}>
              توقف و تأخیر — قرارداد {toFarsiDigits(contract.number || '')}
            </Typography>
            <Typography style={{ fontSize: 11.5, opacity: 0.85 }}>{contract.subject}</Typography>
          </Box>
          <Box style={{ flex: 1 }} />
          <Chip label={report.state_label}
            style={{ background: 'rgba(255,255,255,.22)', color: '#fff', fontWeight: 700 }} />
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}
            style={{ color: '#fff', borderColor: 'rgba(255,255,255,.65)' }}>
            چاپ
          </Button>
          <Button variant="outlined" onClick={openMinutes}
            style={{ color: '#fff', borderColor: 'rgba(255,255,255,.65)', marginRight: 8 }}>
            صورت‌جلسه
          </Button>
        </Box>
      </Box>

      <Box className={classes.stepBar}>
        <Stepper activeStep={step} alternativeLabel>
          {['مشخصات', 'تقویم توقف', 'ردیف‌های تأخیر', 'تأیید و گردش'].map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </Box>

      <Card className={`delay-step ${(step === 0 || printAll) ? 'active' : ''}`} style={{ marginBottom: 12 }}>
        <CardContent style={{ paddingBottom: 10 }}>
          <Typography style={{ fontSize: 13, color: '#555' }}>{contract.subject}</Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 8 }}>
            <Chip size="small" label={`تاریخ قرارداد: ${jLabel(contract.date)}`} />
            <Chip size="small" label={`مدت: ${contract.duration_months ? toFarsiDigits(contract.duration_months) + ' ماه' : '—'}`} />
            <Chip size="small" label={`تحویل زمین: ${jLabel(contract.land_delivery_date)}`} />
            <Chip size="small" label={`خاتمهٔ مجاز: ${jLabel(contract.legal_end_date)}`} />
            <Chip size="small" label={`تحویل موقت: ${jLabel(contract.temp_delivery_date)}`} />
          </Box>
        </CardContent>
      </Card>

      <Card className={`delay-step ${(step === 3 || printAll) ? 'active' : ''}`} style={{ marginBottom: 12, borderRight: '5px solid #1a237e' }}>
        <CardContent style={{ paddingBottom: 10 }}>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
            <Chip label={`وضعیت: ${report.state_label}`} style={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 800 }} />
            {!access.can_edit && (
              <Chip size="small" label="ویرایش قفل است" style={{ background: '#eceff1', color: '#546e7a', fontWeight: 700 }} />
            )}
            {report.last_comment && (
              <Typography style={{ fontSize: 12.5, color: '#b71c1c' }}>
                آخرین توضیح: {report.last_comment}
              </Typography>
            )}
          </Box>

          {(report.actions || []).length > 0 && (
            <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 10, alignItems: 'center' }}>
              {canSelect && (
                <Typography style={{ fontSize: 12, color: '#b71c1c', width: '100%' }}>
                  برای ایراد: ردیف‌های جدول و روزهای تقویم را انتخاب کن، توضیح بنویس و «ایراد» را بزن.
                </Typography>
              )}
              <TextField size="small" variant="outlined" placeholder="توضیح (برای ایراد الزامی است)"
                value={comment} onChange={(e) => setComment(e.target.value)}
                style={{ flex: 1, minWidth: 240 }} />
              {(report.actions || []).map((a) => (
                <Button key={a.action} size="small" variant="contained"
                  color={a.action.indexOf('reject') >= 0 ? 'secondary' : 'primary'}
                  disabled={busy} onClick={() => doAction(a.action)}>
                  {a.label}
                </Button>
              ))}
            </Box>
          )}

          {(report.events || []).length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12.5, color: '#3949ab', fontWeight: 700 }}>
                تاریخچهٔ گردش ({toFarsiDigits(report.events.length)})
              </summary>
              <ul style={{ margin: '8px 0 0', paddingRight: 18, fontSize: 12, lineHeight: 2 }}>
                {(report.events || []).slice().reverse().map((e, i) => (
                  <li key={i}>
                    <b>{e.action_label}</b> — {e.actor || '—'} — {jLabel((e.at || '').slice(0, 10))} {(e.at || '').slice(11, 16)}
                    {e.comment ? ` — ${e.comment}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </CardContent>
      </Card>

      <Grid className={`delay-step ${(step === 0 || printAll) ? 'active' : ''}`} container spacing={2} style={{ marginBottom: 8 }}>
        {[
          { t: 'روزهای توقف (تأییدشده)', v: summary.stop_days_approved },
          { t: 'در انتظار تأیید', v: summary.stop_days_pending },
          { t: 'مدت واقعی تأخیر', v: summary.actual },
          { t: 'همپوشانی', v: summary.overlap },
          { t: 'تثبیت مجاز', v: summary.fixed_justified },
          { t: 'تثبیت غیرمجاز', v: summary.fixed_unjustified },
        ].map((k) => (
          <Grid item xs={6} sm={2} key={k.t}>
            <Card><CardContent style={{ padding: 10, textAlign: 'center' }}>
              <Typography style={{ fontSize: 22, fontWeight: 800 }}>{toFarsiDigits(k.v || 0)}</Typography>
              <Typography style={{ fontSize: 11, color: '#7a80a3' }}>{k.t}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
        <Grid item xs={12} sm={4}>
          <Card><CardContent style={{ padding: 10, textAlign: 'center' }}>
            <Typography style={{ fontSize: 13, fontWeight: 700 }}>
              خاتمهٔ محاسبه‌شده: {jLabel(summary.new_end_date)}
            </Typography>
            <Typography style={{ fontSize: 11, color: '#7a80a3' }}>
              مبنا: {jLabel(summary.base_end_date)}
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Card className={`delay-step ${(step === 1 || printAll) ? 'active' : ''}`} style={{ marginBottom: 12 }}>
        <CardContent>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8, marginBottom: 10 }}>
            <Typography style={{ fontWeight: 800 }}>تقویم توقف</Typography>
            <Select value={jm} onChange={(e) => setJm(e.target.value)} style={{ height: 32, minWidth: 90 }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <MenuItem key={m} value={m}>{['فروردین','اردیبهشت','خرداد','تیر','مرداد','شهریور','مهر','آبان','آذر','دی','بهمن','اسفند'][m - 1]}</MenuItem>
              ))}
            </Select>
            <Select value={jy} onChange={(e) => setJy(e.target.value)} style={{ height: 32, minWidth: 80 }}>
              {[jy - 1, jy, jy + 1].map((y) => <MenuItem key={y} value={y}>{toFarsiDigits(y)}</MenuItem>)}
            </Select>
            <Select value={kind} onChange={(e) => setKind(e.target.value)} style={{ height: 32, minWidth: 130 }}>
              {STAY_KINDS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
            </Select>
            <Box style={{ flex: 1 }} />
            <span className={classes.legend}><i className={classes.dot} style={{ background: '#e8f5e9', border: '1px solid #a5d6a7' }} /> تأییدشده</span>
            <span className={classes.legend}><i className={classes.dot} style={{ background: '#fff8e1', border: '1px solid #ffe082' }} /> در انتظار</span>
            <span className={classes.legend}><i className={classes.dot} style={{ background: '#ffebee', border: '1px solid #ef9a9a' }} /> ردشده</span>
            <span className={classes.legend}><i className={classes.dot} style={{ background: '#f3e5f5', border: '1px solid #ce93d8' }} /> تعطیل رسمی</span>
          </Box>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {WEEKDAYS.map((w) => <div key={w} className={classes.th}>{w}</div>)}
            {Array.from({ length: startCol }, (_, i) => <div key={'e' + i} className={classes.cell + ' ' + classes.cellEmpty} />)}
            {monthDays.map((d) => {
              const s = stopMap[d.iso]
              const p = pending[d.iso]
              return (
                <Tooltip key={d.iso} title={s ? `${s.kind_label}${s.reason ? ' — ' + s.reason : ''} (${s.status_label})` : p ? 'در انتظار ذخیره' : jLabel(d.iso)}>
                  <div className={classes.cell + ' ' + cellClass(d.iso)} onClick={() => toggleDay(d.iso)}>
                    <b style={{ fontSize: 13 }}>{toFarsiDigits(d.day)}</b>
                    {s && <span style={{ fontSize: 9.5 }}>{s.kind_label}</span>}
                    {!s && p && <span style={{ fontSize: 9.5 }}>{STAY_KINDS.find((k) => k.value === p.kind)?.label}</span>}
                  </div>
                </Tooltip>
              )
            })}
          </div>

          {Object.keys(pending).length > 0 && (
            <Box style={{ marginTop: 10, padding: 10, background: '#fafbff', borderRadius: 8 }}>
              <Typography style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                جزئیات روزهای انتخاب‌شده (اختیاری)
              </Typography>
              {Object.entries(pending).map(([date, v]) => (
                <Box key={date} display="flex" style={{ gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <Chip size="small" label={jLabel(date)} />
                  <TextField size="small" variant="outlined" placeholder="علت" value={v.reason}
                    onChange={(e) => setPending((p) => ({ ...p, [date]: { ...p[date], reason: e.target.value } }))}
                    style={{ flex: 1 }} />
                  <TextField size="small" variant="outlined" placeholder="شمارهٔ مستند" value={v.document_no}
                    onChange={(e) => setPending((p) => ({ ...p, [date]: { ...p[date], document_no: e.target.value } }))}
                    style={{ width: 160 }} />
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" style={{ gap: 8, marginTop: 10 }}>
            {canEdit && (
              <Button variant="contained" color="primary" size="small" startIcon={<SaveIcon />}
                disabled={!dirty || busy} onClick={saveStops}>ذخیرهٔ توقف‌ها</Button>
            )}
            {canVerify && (
              <>
                <Button variant="outlined" size="small" startIcon={<CheckIcon />} disabled={busy}
                  onClick={() => verifyStops('approved')}>تأیید همهٔ در انتظار</Button>
                <Button variant="outlined" size="small" color="secondary" disabled={busy}
                  onClick={() => verifyStops('rejected')}>رد همه</Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card className={`delay-step ${(step === 2 || printAll) ? 'active' : ''}`}>
        <CardContent>
          <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
            <Typography style={{ fontWeight: 800 }}>ردیف‌های تأخیر (کمیسیون)</Typography>
            <Box style={{ flex: 1 }} />
            {canEdit && <Button size="small" variant="contained" color="primary" startIcon={<SaveIcon />} disabled={busy} onClick={saveLines}>ذخیرهٔ ردیف‌ها</Button>}
            {canVerify && <Button size="small" variant="outlined" style={{ marginRight: 8 }} onClick={() => verifyLines('approved')}>تأیید</Button>}
          </Box>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  {[...(canSelect ? ['ایراد'] : []), 'بند', 'شرح', 'واقعی', 'همپوشانی', 'تثبیت', 'مجاز', 'شمارهٔ مستند', 'وضعیت'].map((h) => (
                    <th key={h} style={{ background: '#1a237e', color: '#fff', padding: 6 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.clauses || []).map((cl) => {
                  const l = lineValue(cl.clause_no)
                  const fixed = Math.max(0, (Number(l.actual_days) || 0) - (Number(l.overlap_days) || 0))
                  const saved = ((data.lines || []).find((x) => x.clause_no === cl.clause_no))
                  return (
                    <tr key={cl.clause_no} style={{
                      borderBottom: '1px solid #eef0f8',
                      background: (openLines.includes(cl.clause_no) || objLines.includes(cl.clause_no)) ? '#ffebee' : undefined,
                    }}>
                      {canSelect && (
                        <td style={{ padding: 4, textAlign: 'center' }}>
                          <input type="checkbox" checked={objLines.includes(cl.clause_no)}
                            onChange={(e) => setObjLines((s) => (e.target.checked ? [...s, cl.clause_no] : s.filter((x) => x !== cl.clause_no)))} />
                        </td>
                      )}
                      <td style={{ padding: 4, textAlign: 'center' }}>{toFarsiDigits(cl.clause_no)}</td>
                      <td style={{ padding: 4 }}>{cl.title}</td>
                      <td style={{ padding: 4 }}>
                        <TextField type="number" size="small" value={l.actual_days}
                          onChange={(e) => setLine(cl.clause_no, { actual_days: e.target.value })}
                          disabled={!canEdit} style={{ width: 70 }} />
                      </td>
                      <td style={{ padding: 4 }}>
                        <TextField type="number" size="small" value={l.overlap_days}
                          onChange={(e) => setLine(cl.clause_no, { overlap_days: e.target.value })}
                          disabled={!canEdit} style={{ width: 70 }} />
                      </td>
                      <td style={{ padding: 4, textAlign: 'center', fontWeight: 700 }}>{toFarsiDigits(fixed)}</td>
                      <td style={{ padding: 4, textAlign: 'center' }}>
                        <input type="checkbox" checked={!!l.justified}
                          onChange={(e) => setLine(cl.clause_no, { justified: e.target.checked })}
                          disabled={!canEdit} />
                      </td>
                      <td style={{ padding: 4 }}>
                        <TextField size="small" value={l.document_no}
                          onChange={(e) => setLine(cl.clause_no, { document_no: e.target.value })}
                          disabled={!canEdit} style={{ width: 120 }} />
                      </td>
                      <td style={{ padding: 4, textAlign: 'center' }}>
                        {saved ? (
                          <Chip size="small" label={saved.status_label}
                            style={{ background: saved.status === 'approved' ? '#e8f5e9' : saved.status === 'rejected' ? '#ffebee' : '#fff8e1', fontSize: 11 }} />
                        ) : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" style={{ marginTop: 14 }}>
        <Button disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
          مرحلهٔ قبل
        </Button>
        {step < 3 ? (
          <Button variant="contained" color="primary" onClick={() => setStep((s) => Math.min(3, s + 1))}>
            مرحلهٔ بعد
          </Button>
        ) : (
          <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={handlePrint}>
            چاپ نهایی
          </Button>
        )}
      </Box>

      <Typography style={{ textAlign: 'center', fontSize: 10.5, color: '#8a92b2', marginTop: 18 }}>
        {REPORT_FOOTER_TEXT}
      </Typography>

        </Box>
      </Box>

      <style>{`
        .delay-step { display: none; }
        .delay-step.active { display: block; }
        @media print {
          .delay-step { display: block !important; }
          body * { visibility: hidden; }
          #delay-print, #delay-print * { visibility: visible; }
          #delay-print { position: absolute; inset: 0; }
          button { display: none !important; }
        }
      `}</style>
    </Box>
    </Layout>
  )
}

export default DelayPage
